import React, { useState } from 'react';
import axios from 'axios';

export default function Statistics() {
  const [selectedType, setSelectedType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statisticsData, setStatisticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('statistics');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleFetch = async (page = currentPage) => {
    if (!selectedType || !startDate || !endDate) {
      setError('Please select a type and specify both start and end dates.');
      return;
    }
    setLoading(true);
    setError('');
    setStatisticsData(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStatistics`, {
        params: {
          type: selectedType,
          startDate,
          endDate,
          search: searchTerm,
          page: page.toString(),
          limit: '10',
        },
      });
      if (response.data.success) {
        setStatisticsData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to fetch statistics.');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError('An error occurred while fetching statistics.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleFetch(newPage);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return typeof amount === 'number' ? amount.toFixed(2) : '0.00';
  };

  const getTypeLabel = (type) => {
    return type === 'video_payment' ? 'Video Payment' : type === 'live_payment' ? 'Live Payment' : 'Total Payment';
  };

  const handleExportReceiptPackage = async () => {
    if (!exportStartDate || !exportEndDate) {
      setExportError('Please select both start and end dates.');
      return;
    }
    if (exportStartDate > exportEndDate) {
      setExportError('Start date must be on or before end date.');
      return;
    }

    setExporting(true);
    setExportError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/exportReceiptPackage`, {
        params: { startDate: exportStartDate, endDate: exportEndDate },
        responseType: 'blob',
        timeout: 180000,
      });
      const disposition = response.headers['content-disposition'];
      let filename = `receipt-package_${exportStartDate}_${exportEndDate}.zip`;
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = disposition.match(/filename="?([^"]+)"?/);
        if (matches && matches[1]) filename = matches[1];
      }
      const file = new Blob([response.data], { type: 'application/zip' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (exportErr) {
      console.error('Error exporting receipt package:', exportErr);
      let message = 'Failed to export receipt package.';
      const data = exportErr.response?.data;
      if (data instanceof Blob) {
        try {
          const body = JSON.parse(await data.text());
          message = body.error || message;
        } catch (parseErr) {
          message = exportErr.message || message;
        }
      } else if (data?.error) {
        message = data.error;
      }
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 min-h-screen overflow-y-auto">
      <h1 className="text-2xl font-bold">Statistics</h1>
      <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-5 py-2.5 rounded-md font-medium ${
            activeTab === 'statistics'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 border border-gray-300'
          }`}
        >
          Statistics
        </button>
        <button
          onClick={() => setActiveTab('accounting')}
          className={`px-5 py-2.5 rounded-md font-medium ${
            activeTab === 'accounting'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 border border-gray-300'
          }`}
        >
          Accounting
        </button>
      </div>
      {activeTab === 'accounting' && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 w-full">
          <h2 className="text-2xl font-semibold text-gray-900">One-click export: receipt package</h2>
          <p className="mt-2 mb-6 text-gray-600 max-w-3xl">
            Exports paid invoices whose received date is inside the selected range. Unzip the whole folder, then open index.html in a browser to view the slips. The Excel links only work in Microsoft Excel, not Apple Numbers.
          </p>
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div>
              <label className="block mb-1 text-gray-700 font-medium" htmlFor="exportStartDate">Start Date</label>
              <input
                type="date"
                id="exportStartDate"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-gray-700 font-medium" htmlFor="exportEndDate">End Date</label>
              <input
                type="date"
                id="exportEndDate"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleExportReceiptPackage}
              disabled={exporting}
              className={`px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${exporting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {exporting ? 'Exporting...' : 'Export zip'}
            </button>
          </div>
          {exportError && <p className="mt-4 text-red-600 font-medium">{exportError}</p>}
        </div>
      )}
      {activeTab === 'statistics' && (
      <>
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4 mt-2 md:mt-0 w-full md:w-auto">
          <div className="flex items-center">
            <label className="mr-2 text-gray-700 font-medium" htmlFor="type">Type</label>
            <select
              id="type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a type</option>
              <option value="video_payment">Video Payment</option>
              <option value="live_payment">Live Payment</option>
              <option value="total_payment">Total Payment</option>
            </select>
          </div>
          <div className="flex items-center mt-2 md:mt-0">
            <label className="mr-2 text-gray-700 font-medium" htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mt-2 md:mt-0">
            <label className="mr-2 text-gray-700 font-medium" htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mt-2 md:mt-0">
            <label className="mr-2 text-gray-700 font-medium" htmlFor="search">Student Search</label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by student name"
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => { setCurrentPage(1); handleFetch(1); }}
            className={`mt-2 md:mt-0 md:ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-4 text-red-600 font-medium">
          {error}
        </div>
      )}
      {statisticsData && (
        <div className="mt-6 space-y-6">
          {/* Summary Section */}
          <div>
            <h2 className="text-xl font-semibold mb-2">Summary</h2>
            <div className="bg-white border border-gray-200 rounded-md p-4">
              <div className="mb-4">
                <p className="text-lg font-medium">Type: {getTypeLabel(statisticsData.type)}</p>
                <p className="text-lg font-medium">Period: {formatDate(statisticsData.start_date)} to {formatDate(statisticsData.end_date)}</p>
                <p className="text-lg font-medium">Total Amount: {formatAmount(statisticsData.total_amount)}</p>
                <p className="text-lg font-medium">Total Transactions: {statisticsData.total_count || 0}</p>
              </div>
              <h3 className="text-md font-semibold mb-2">Breakdown by Payment Type</h3>
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="px-4 py-2 border">Payment Type</th>
                    <th className="px-4 py-2 border">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center">
                    <td className="px-4 py-2 border">Cash</td>
                    <td className="px-4 py-2 border">{formatAmount(statisticsData.breakdown?.cash || 0)}</td>
                  </tr>
                  <tr className="text-center">
                    <td className="px-4 py-2 border">Bank-in</td>
                    <td className="px-4 py-2 border">{formatAmount(statisticsData.breakdown?.['bank-in'] || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Transactions Section */}
          <div>
            <h2 className="text-xl font-semibold mb-2">Transactions</h2>
            {statisticsData.transactions && statisticsData.transactions.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Amount</th>
                      <th className="px-4 py-2 border">Payment Type</th>
                      <th className="px-4 py-2 border">Collection Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statisticsData.transactions.map((transaction, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="px-4 py-2 border">{transaction.student_string}</td>
                        <td className="px-4 py-2 border">{formatAmount(transaction.total_amount)}</td>
                        <td className="px-4 py-2 border">{transaction.payment_type}</td>
                        <td className="px-4 py-2 border">{formatDate(transaction.collection_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {statisticsData.total_pages > 1 && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {statisticsData.total_pages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === statisticsData.total_pages}
                      className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No transactions found for the selected criteria.</p>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}